import type { FreeLayoutPluginContext } from '@flowgram.ai/free-layout-editor';

let teamFlowContext: FreeLayoutPluginContext | undefined;
const teamFlowContextListeners = new Set<(ctx: FreeLayoutPluginContext | undefined) => void>();

/**
 * 注册当前 Team Flow 画布上下文，并唤醒等待画布挂载的跨面板任务。
 */
export function registerTeamFlowContext(ctx: FreeLayoutPluginContext | undefined) {
  teamFlowContext = ctx;
  teamFlowContextListeners.forEach((listener) => {
    listener(ctx);
  });
}

/**
 * 读取已注册的 Team Flow 画布上下文；画布未挂载时返回 undefined。
 */
export function getRegisteredTeamFlowContext() {
  return teamFlowContext;
}

/**
 * 订阅 Team Flow 画布上下文变化；画布卸载或切房间时会收到 undefined。
 */
export function subscribeTeamFlowContextChange(
  listener: (ctx: FreeLayoutPluginContext | undefined) => void,
): () => void {
  teamFlowContextListeners.add(listener);
  return () => {
    teamFlowContextListeners.delete(listener);
  };
}

/**
 * 等待 Team Flow 画布上下文注册，用于自动发送早于画布挂载的场景兜底。
 */
export function waitRegisteredTeamFlowContext(timeoutMs = 3000): Promise<FreeLayoutPluginContext | undefined> {
  if (teamFlowContext?.document) {
    return Promise.resolve(teamFlowContext);
  }

  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    /** 清理本次等待的监听和超时器，避免页面切房间或卸载后残留。 */
    const cleanup = (listener: (ctx: FreeLayoutPluginContext | undefined) => void) => {
      teamFlowContextListeners.delete(listener);
      if (timer) {
        clearTimeout(timer);
      }
    };

    /** 收到有效画布上下文后恢复挂起的资源同步任务。 */
    const listener = (ctx: FreeLayoutPluginContext | undefined) => {
      if (!ctx?.document) {
        return;
      }
      cleanup(listener);
      resolve(ctx);
    };

    teamFlowContextListeners.add(listener);
    timer = setTimeout(() => {
      cleanup(listener);
      resolve(undefined);
    }, timeoutMs);
  });
}
